import React from "react";
import { statusColorCodes } from '../../../common/utils/commonUtils';
import EllipsisText from "../../../common/ellipsisText/EllipsisText";
import FileDownloadButton from "../../../common/ellipsisText/FileDownloadButton";
import ColumnHeader from "../../../common/table/ColumnHeader";
import StatusInfo from "../../../common/table/StatusInfo";
import { TASK_COLUMN_INFO, EXCALIBUR_TASK_STATUS_INFO, FILING_TASK_STATUS_INFO } from "../../../common/constants/columnInfo";
import { ROUTE_PATH_EXCALIBUR_TRANSACTION_DETAILS_PAGE } from "../../../common/constants/route_paths";
import { getFileNameFromS3Key } from "../utils/Utils";
import {
    FILE_TYPE_INPUT_FILE,
    FILE_TYPE_OUTPUT_FILE,
    FILE_TYPE_RECEIPT,
    OUTPUT_FILE_METADATA_KEY_PAIRS
} from "../../../common/constants/constants";

const DOWNLOADING_LABEL = "Downloading";

/**
 * Identity of one in-flight per-task download.
 *
 * A row carries two independent download cells, so the key pairs the task with the file type being
 * fetched: downloading a row's input file must not disable that same row's output file cell.
 *
 * @param {string} taskId per-task identifier of the row being downloaded
 * @param {string} fileType the file type that cell requests
 * @returns {string} the key identifying that one cell's download
 */
export const taskDownloadKey = (taskId, fileType) => `${taskId}:${fileType}`;

/**
 * True when the given route belongs to the transformation family rather than the filing family.
 *
 * One page serves both, so the route is what decides the second download column's meaning: reached
 * via /excalibur/transactiondetails the transaction is a transformation and the column is its
 * Generated Report; via /filing/transactiondetails it is a filing and the column is its Submission
 * Receipt. This is the same discriminator the breadcrumb already uses.
 *
 * @param {string} [pathname] the current route path
 * @returns {boolean} true for a transformation route, false for anything else
 */
export const isTransformationRoute = pathname =>
    Boolean(pathname) && pathname.startsWith(ROUTE_PATH_EXCALIBUR_TRANSACTION_DETAILS_PAGE);

/** Label of a transaction's second file; shared by the task table header and the Transaction Details panel. */
export const getGeneratedFileLabel = isTransformation =>
    isTransformation ? "Generated Report" : "Submission Receipt";

/** Label of a task's input: only a filing fed by a transformation reads a generated report; every other task reads the upload. */
export const getTaskInputFileLabel = (isTransformation, parentAuditRow) =>
    !isTransformation && parentAuditRow?.additionalDataMap?.ExcaliburTransformationId ? "Generated Report" : "Input File";

// True when the task row carries an input file location Excalibur can resolve.
const hasInputFile = record => Boolean(record?.taskId && record?.inputFilePath && record?.inputFileBucket);

// True when the task row carries an output file location Excalibur can resolve, either on its
// own columns or via one of the metadata pointer pairs older rows use.
//
// This one check serves both FILE_TYPE_OUTPUT_FILE and FILE_TYPE_RECEIPT because Excalibur
// resolves them identically on the task-row path: GetFileDownloadURLComponent sends RECEIPT and
// OUTPUT_FILE alike to getOutputFileUrlsFromTaskRow, which reads outputFilePath/outputFileBucket
// and falls back to the same METADATA_OUTPUT_POINTER_KEY_PAIRS mirrored here. There is no
// receipt-specific pointer on a task row to check for. (Receipt and output file do diverge on
// Excalibur's per-transaction audit-record path, which this page does not use.)
const hasOutputFile = record => {
    if (!record?.taskId) {
        return false;
    }
    if (record.outputFilePath && record.outputFileBucket) {
        return true;
    }
    const metadata = record.metadata;
    if (!metadata) {
        return false;
    }
    return OUTPUT_FILE_METADATA_KEY_PAIRS.some(([pathKey, bucketKey]) => metadata[pathKey] && metadata[bucketKey]);
};

// The output file's key, from the same place hasOutputFile finds it; null when there is none.
const getOutputFilePath = record => {
    if (record?.outputFilePath && record?.outputFileBucket) {
        return record.outputFilePath;
    }
    const metadata = record?.metadata;
    const pair = metadata && OUTPUT_FILE_METADATA_KEY_PAIRS.find(([pathKey, bucketKey]) => metadata[pathKey] && metadata[bucketKey]);
    return pair ? metadata[pair[0]] : null;
};

// Renders one download cell, or nothing when the file is not resolvable, so an unavailable file
// never issues a request that fails.
//
// While this cell's own download is in flight it shows a spinner and stops accepting clicks, so a
// second click cannot queue a duplicate request for a file already being fetched.
const downloadCell = (available, inProgress, onDownload, fileName = null) => available
    ? <FileDownloadButton fileName={fileName} fallback="File"
                          loading={inProgress} loadingText={DOWNLOADING_LABEL} onClick={onDownload} />
    : null;

// Dedicated per-task column set for the Transaction Details page.
//
// Both download cells call `downloadTaskFile(taskId, fileType)`, which fetches an Excalibur-signed
// URL scoped to that one task. `isTransformation` selects the second column's meaning: a filing
// transaction produces a submission RECEIPT, a transformation transaction produces an OUTPUT_FILE
// (labelled Generated Report -- see getGeneratedFileLabel). The first column's label comes from
// the parent record -- see getTaskInputFileLabel.
// `downloadingTaskKey` names the single cell whose download is currently in flight, if any.
export const getTransactionDetailColumnDefinitions = ({
    downloadTaskFile,
    isTransformation = false,
    downloadingTaskKey = null,
    parentAuditRow = null
}) => [
    {
        id: "taskId",
        header: <ColumnHeader title="Task ID" info={TASK_COLUMN_INFO.taskId} />,
        cell: record => record.taskId,
        width: 360,
        minWidth: 360
    },
    {
        id: "taxIdentifier",
        header: <ColumnHeader title="Tax Identifier" info={TASK_COLUMN_INFO.taxIdentifier} />,
        cell: record => record.taxIdentifier || "",
        width: 150,
        minWidth: 150
    },
    {
        id: "status",
        header: <ColumnHeader title="Status" infoLabel={TASK_COLUMN_INFO.status}
                              info={<StatusInfo intro={TASK_COLUMN_INFO.status}
                                                statuses={isTransformation ? EXCALIBUR_TASK_STATUS_INFO : FILING_TASK_STATUS_INFO} />} />,
        cell: record => (<div style={statusColorCodes(record.status)}>{record.status}</div>),
        width: 130,
        minWidth: 130
    },
    {
        id: "taskInputFile",
        header: <ColumnHeader title={getTaskInputFileLabel(isTransformation, parentAuditRow)} info={TASK_COLUMN_INFO.taskInputFile} />,
        cell: record => downloadCell(
            hasInputFile(record),
            downloadingTaskKey === taskDownloadKey(record.taskId, FILE_TYPE_INPUT_FILE),
            () => downloadTaskFile(record.taskId, FILE_TYPE_INPUT_FILE),
            getFileNameFromS3Key(record.inputFilePath)
        ),
        width: 260,
        minWidth: 160
    },
    {
        id: "taskSubmissionReceipt",
        header: <ColumnHeader title={getGeneratedFileLabel(isTransformation)}
                              info={isTransformation ? TASK_COLUMN_INFO.generatedReport : TASK_COLUMN_INFO.submissionReceipt} />,
        cell: record => {
            const fileType = isTransformation ? FILE_TYPE_OUTPUT_FILE : FILE_TYPE_RECEIPT;
            return downloadCell(
                hasOutputFile(record),
                downloadingTaskKey === taskDownloadKey(record.taskId, fileType),
                () => downloadTaskFile(record.taskId, fileType),
                getFileNameFromS3Key(getOutputFilePath(record))
            );
        },
        width: 260,
        minWidth: 160
    },
    {
        id: "errorDetails",
        header: <ColumnHeader title="Error Details" info={TASK_COLUMN_INFO.errorDetails} />,
        cell: record => <EllipsisText text={record.errorDetails} fill />,
        width: 320,
        minWidth: 320
    }
];

export const transactionDetailVisibleColumns = [
    "taskId",
    "taxIdentifier",
    "status",
    "taskInputFile",
    "taskSubmissionReceipt",
    "errorDetails"
];
